import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Input, Select, Tag, Tooltip, message } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { COLORS } from '../../../constants/colors';
import caseService from '../../../services/CaseService';
import OutreachTableSkeleton from './OutreachTableSkeleton';
import { FilterBar, CaseTable, OutreachHeartLoader } from '../styled';

const TYPE_META = {
  outreach: { label: 'Outreach', color: 'blue' },
  campaign: { label: 'Campaign', color: 'purple' },
  inbox: { label: 'Inbox reply', color: 'cyan' },
  results: { label: 'Results', color: 'green' },
  'sign-in': { label: 'Sign-in link', color: 'geekblue' },
  'password-reset': { label: 'Password reset', color: 'gold' },
  automation: { label: 'Automation', color: 'magenta' },
  survey: { label: 'Survey', color: 'lime' },
  other: { label: 'Other', color: 'default' }
};

const STATUS_META = {
  sent: { label: 'Sent', color: 'default' },
  delivered: { label: 'Delivered', color: 'blue' },
  opened: { label: 'Opened', color: 'green' },
  clicked: { label: 'Clicked', color: 'green' },
  failed: { label: 'Failed', color: 'red' },
  invalid: { label: 'Invalid address', color: 'red' },
  bounced: { label: 'Bounced', color: 'red' },
  dropped: { label: 'Dropped', color: 'red' },
  spam: { label: 'Marked spam', color: 'volcano' },
  unsubscribed: { label: 'Unsubscribed', color: 'orange' }
};

/**
 * Outreach → Activity → "All emails": every email the platform has sent
 * (outreach, campaigns, results, sign-in links, password resets, …).
 * Rows tied to a member open that member's email panel via onRowClick.
 */
const EmailLogTable = ({ onRowClick }) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [typeCounts, setTypeCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [type, setType] = useState('all');
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, type, status]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, pageSize, type, status };
      if (debouncedSearch) params.search = debouncedSearch;
      const res = await caseService.getEmailLog(params);
      setItems(res.data?.items || []);
      setTotal(res.data?.total || 0);
      setTypeCounts(res.data?.typeCounts || {});
    } catch {
      message.error('Failed to load email log');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, type, status, debouncedSearch]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = [
    {
      title: 'Sent',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (v) => (v ? new Date(v).toLocaleString() : '—')
    },
    {
      title: 'To',
      dataIndex: 'to',
      key: 'to',
      width: 240,
      ellipsis: true
    },
    {
      title: 'Subject',
      dataIndex: 'subject',
      key: 'subject',
      ellipsis: true,
      render: (v) => v || <span style={{ color: COLORS.textMuted }}>(no subject)</span>
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 140,
      render: (v) => {
        const meta = TYPE_META[v] || TYPE_META.other;
        return <Tag color={meta.color}>{meta.label}</Tag>;
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 150,
      render: (v, record) => {
        const meta = STATUS_META[v] || STATUS_META.sent;
        const tag = <Tag color={meta.color}>{meta.label}</Tag>;
        return record.error ? <Tooltip title={record.error}>{tag}</Tooltip> : tag;
      }
    }
  ];

  const typeOptions = [
    { label: 'All types', value: 'all' },
    ...Object.entries(TYPE_META).map(([value, meta]) => ({
      label: `${meta.label}${typeCounts[value] ? ` (${typeCounts[value]})` : ''}`,
      value
    }))
  ];

  const statusOptions = [
    { label: 'All statuses', value: 'all' },
    ...Object.entries(STATUS_META).map(([value, meta]) => ({ label: meta.label, value }))
  ];

  return (
    <div>
      <FilterBar>
        <Row gutter={[12, 12]} align="middle">
          <Col flex="auto">
            <Input
              placeholder="Search by recipient email or subject..."
              prefix={<FontAwesomeIcon icon={faMagnifyingGlass} style={{ color: COLORS.textMuted }} />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
              style={{ height: 40, borderRadius: 8 }}
            />
          </Col>
          <Col>
            <Select value={type} onChange={setType} options={typeOptions} style={{ width: 190 }} />
          </Col>
          <Col>
            <Select value={status} onChange={setStatus} options={statusOptions} style={{ width: 170 }} />
          </Col>
        </Row>
      </FilterBar>

      {loading && items.length === 0 ? (
        <OutreachTableSkeleton columns={columns} rows={8} scroll={{ x: 900 }} />
      ) : (
        <CaseTable
          columns={columns}
          dataSource={items}
          rowKey="_id"
          loading={{ spinning: loading, indicator: <OutreachHeartLoader /> }}
          onRow={(record) => ({
            style: { cursor: record.patientId ? 'pointer' : 'default' },
            onClick: () => record.patientId && onRowClick?.(record)
          })}
          locale={{ emptyText: 'No emails logged yet. Emails appear here as the platform sends them.' }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (t, range) => `${range[0]}-${range[1]} of ${t} emails`,
            onChange: (p, size) => {
              setPage(p);
              setPageSize(size);
            }
          }}
          className="event-management-pagination"
          scroll={{ x: 900 }}
        />
      )}
    </div>
  );
};

export default EmailLogTable;
